import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dispatch } from 'redux';
import styled from 'styled-components';

import { sortByMixedString } from '@utils/miscellaneous/sortings';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import Table, { IndeterminateCheckbox } from '../../../components/common/react-table/Table';
import { updateAdminPrivileges } from '../../../actions/admin/adminProvisionActionCreators';
import { IProvisioningRules } from './types/AdminProvision.interface';

const StyledTableContainer = styled.div`
    margin: 10px auto;

    th {
        color: #444;
    }

    td input {
        height: max-content;
    }
`;

const AdminProvisionTable = () => {
    const intl = useIntl();
    const dispatch: Dispatch<any> = useAppDispatch();
    const filteredProvisioningRules = useAppSelector(state => state.adminProvision.filteredProvisioningRules);

    const handleAllowUpdateChange = (provisioningRule: IProvisioningRules) => {
        dispatch(updateAdminPrivileges(provisioningRule));
    };

    const columns = useMemo(
        () => [
            {
                Header: 'admin.provision.table.column.provisioningRole',
                accessor: 'provisioning_role_label',
                Cell: ({ row }) => row.original.provisioning_role_label,
                sortType: sortByMixedString,
            },
            {
                Header: 'admin.provision.table.column.managedRole',
                accessor: 'managed_role_label',
                Cell: ({ row }) => row.original.managed_role_label,
                sortType: sortByMixedString,
            },
            {
                Header: 'admin.provision.table.column.allowCreate',
                accessor: 'allow_add',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const [defaultAllowAdd, setDefaultAllowAdd] = useState<Record<string, boolean>>(() =>
                        filteredProvisioningRules.reduce(
                            (acc, privilege) => {
                                acc[privilege.privateId] = privilege.allow_add === true;
                                return acc;
                            },
                            {} as Record<string, boolean>,
                        ),
                    );

                    const handleToggleAllowAdd = useCallback((privateId: string) => {
                        setDefaultAllowAdd(prev => ({
                            ...prev,
                            [privateId]: !prev[privateId],
                        }));
                    }, []);

                    return (
                        <IndeterminateCheckbox
                            isChecked={defaultAllowAdd[row.original.privateId]}
                            onChange={() => {
                                row.original.allow_add = !row.original.allow_add;
                                handleToggleAllowAdd(row.original.privateId);
                                handleAllowUpdateChange(row.original);
                            }}
                            aria-label={`${row.original?.privateId} ${intl.formatMessage({ id: 'admin.provision.table.column.allowCreate' })}`}
                        />
                    );
                },
            },
            {
                Header: 'admin.provision.table.column.allowUpdate',
                accessor: 'allow_update',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const [defaultAllowUpdate, setDefaultAllowUpdate] = useState<Record<string, boolean>>(() =>
                        filteredProvisioningRules.reduce(
                            (acc, privilege) => {
                                acc[privilege.privateId] = privilege.allow_update === true;
                                return acc;
                            },
                            {} as Record<string, boolean>,
                        ),
                    );

                    const handleToggleAllowUpdate = useCallback((privateId: string) => {
                        setDefaultAllowUpdate(prev => ({
                            ...prev,
                            [privateId]: !prev[privateId],
                        }));
                    }, []);

                    return (
                        <IndeterminateCheckbox
                            isChecked={defaultAllowUpdate[row.original.privateId]}
                            onChange={() => {
                                row.original.allow_update = !row.original.allow_update;
                                handleToggleAllowUpdate(row.original.privateId);
                                handleAllowUpdateChange(row.original);
                            }}
                            aria-label={`${row.original?.privateId} ${intl.formatMessage({ id: 'admin.provision.table.column.allowUpdate' })}`}
                        />
                    );
                },
            },
            {
                Header: 'admin.provision.table.column.enabled',
                accessor: 'active',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const [defaultEnabled, setDefaultEnabled] = useState<Record<string, boolean>>(() =>
                        filteredProvisioningRules.reduce(
                            (acc, privilege) => {
                                acc[privilege.privateId] = privilege.active === true;
                                return acc;
                            },
                            {} as Record<string, boolean>,
                        ),
                    );

                    const handleToggleEnabled = useCallback((privateId: string) => {
                        setDefaultEnabled(prev => ({
                            ...prev,
                            [privateId]: !prev[privateId],
                        }));
                    }, []);

                    return (
                        <IndeterminateCheckbox
                            isChecked={defaultEnabled[row.original.privateId]}
                            onChange={() => {
                                row.original.active = !row.original.active;
                                handleToggleEnabled(row.original.privateId);
                                handleAllowUpdateChange(row.original);
                            }}
                            aria-label={`${row.original?.privateId} ${intl.formatMessage({ id: 'admin.provision.table.column.enabled' })}`}
                        />
                    );
                },
            },
        ],
        [filteredProvisioningRules],
    );

    return (
        <StyledTableContainer data-testid="admin-provision-table-container">
            <Table
                id="admin-provision-table"
                data-testid="admin-provision-table"
                className="admin-provision-table"
                isClientPagination
                allowSort
                hasPagination
                isShowRowsPerPage
                isShowPaginationHeader
                hasBorders
                variant="simple"
                columns={columns}
                data={filteredProvisioningRules || []}
                totalCount={filteredProvisioningRules?.length || 0}
                clientPageSize={10}
                maxNumPages={10}
                selectionKey="privateId"
                paginationPrevText={intl.formatMessage({ id: 'admin.provision.buttonPrevious' })}
                paginationNextText={intl.formatMessage({ id: 'admin.provision.buttonNext' })}
                emptyMessage={
                    <span data-testid="admin-provision-table-no-record">
                        <FormattedMessage id="admin.provision.table.noMatchFound" />
                    </span>
                }
            />
        </StyledTableContainer>
    );
};

export default AdminProvisionTable;
